import { css } from '@emotion/react';
import { Button, Chip, Stack, Typography } from '@mui/material';
import { ReactNode, useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { ConfirmDeletionDialog } from '../../../components/Dialog/ConfirmDialog/ConfirmDeletionDialog';
import { AppName } from '../../../data-models/integrations-app.data-model';
import { useIntegrationActions } from '../hooks/useIntegrationActions';
import { integrationsStateLoadable } from '../state/IntegrationsState';
import { AppMap } from '../utils/uiAppData';
import { useAtomValue } from 'jotai';

const logoContainerCss = css`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  & > svg {
    max-width: 36px;
    max-height: 36px;
    width: 100%;
    height: 100%;
  }
`;

interface IntegrationDetailHeaderProps {
  appName: AppName;
  description: string;
  /** Override the displayed title (defaults to AppMap.displayName). */
  title?: string;
  /** Additional chips/badges shown to the right of the Connected chip. */
  extraChips?: ReactNode;
}

export function IntegrationDetailHeader({
  appName,
  description,
  title,
  extraChips,
}: IntegrationDetailHeaderProps) {
  const navigate = useNavigate();
  const integrationsLoadable = useAtomValue(integrationsStateLoadable);
  const actions = useIntegrationActions();
  const [showDisconnect, setShowDisconnect] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);

  const integrationsData = integrationsLoadable.state === 'hasData' ? integrationsLoadable.data : undefined;
  const appEntry = integrationsData?.find((app) => app.appName === appName);
  const uiData = AppMap.get(appName);
  const displayName = title ?? uiData?.displayName ?? appName;
  const isConnected = !!appEntry?.isConnected;
  const showDisconnectButton = isConnected && !appEntry?.supportMultipleConnections && !!appEntry?.id;

  const handleDisconnect = useCallback(async () => {
    if (!appEntry?.id) {
      return;
    }
    setDisconnecting(true);
    try {
      await actions.disconnect(appEntry.id);
      setShowDisconnect(false);
      navigate('/integrations/apps');
    } finally {
      setDisconnecting(false);
    }
  }, [actions, appEntry, navigate]);

  if (!uiData) {
    return null;
  }

  return (
    <>
      {showDisconnect && (
        <ConfirmDeletionDialog
          open
          title={`Disconnect ${uiData.displayName}?`}
          onClose={() => setShowDisconnect(false)}
          onConfirm={handleDisconnect}
          loading={disconnecting}
          confirmLabel='Yes, Disconnect'
        >
          <Typography variant='body2' color='text.secondary'>
            Are you sure you want to disconnect? Disconnecting this integration will affect all users in your
            organization.
          </Typography>
        </ConfirmDeletionDialog>
      )}
      <Stack direction='row' alignItems='center' columnGap='0.5rem' flexWrap='wrap'>
        <div css={logoContainerCss}>{uiData.icon}</div>
        <Typography variant='h4'>{displayName}</Typography>
        {isConnected && <Chip label='Connected' size='small' sx={{ borderRadius: '4px' }} />}
        {extraChips}
        {showDisconnectButton && (
          <Button
            color='error'
            variant='contained'
            size='small'
            sx={{ ml: 'auto' }}
            onClick={() => setShowDisconnect(true)}
          >
            Disconnect
          </Button>
        )}
      </Stack>
      <Typography variant='body2' color='text.secondary' mt='0.5rem'>
        {description}
      </Typography>
    </>
  );
}
